import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, alpha} from '@mui/material';
import { Box } from '@mui/system';

import { DarkTheme, LightTheme } from './../themes';

interface IThemeContextData {
  themeName: 'light' | 'dark';
  toggleTheme: () => void;
  temaSistema: 'light' | 'dark';
}

const ThemeContext = createContext({} as IThemeContextData);

export const useAppThemeContext = () => {
  return useContext(ThemeContext);
};

interface IAppThemeProviderProps {
  children: React.ReactNode;
}
export const AppThemeProvider: React.FC<IAppThemeProviderProps> = ({ children }) => {
  const [themeName, setThemeName] = useState<'light' | 'dark'>('light');
  const [temaSis, setTemaSis] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    setTemaSis(themeName);
  }, [themeName]);

  const toggleTheme = useCallback(() => {
    setThemeName((oldThemeName) => (oldThemeName === 'light' ? 'dark' : 'light'));
  }, []);

  const theme = useMemo(() => {
    const baseTheme = themeName === 'light' ? LightTheme : DarkTheme;
    
    return createTheme({
      ...baseTheme,
    
      breakpoints: {
        values: {
          xs: 0,
          sm: 700,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
    
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              scrollbarWidth: 'thin',
    
              scrollbarColor:
                themeName === 'dark'
                  ? 'rgba(255,255,255,0.20) transparent'
                  : 'rgba(0,0,0,0.20) transparent',
    
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
    
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
    
              '&::-webkit-scrollbar-thumb': {
                backgroundColor:
                  themeName === 'dark'
                    ? alpha('#fff', 0.18)
                    : alpha('#000', 0.18),
    
                borderRadius: '10px',
              },
    
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor:
                  themeName === 'dark'
                    ? alpha('#fff', 0.30)
                    : alpha('#000', 0.30),
              },
            },
          },
        },
      },
    });
  }, [themeName]);

  return (
    <ThemeContext.Provider value={{ themeName, toggleTheme, temaSistema: temaSis }}>
      <ThemeProvider theme={theme}>
      <CssBaseline />
        <Box width="100vw" height="100vh" bgcolor={theme.palette.background.default}>
          {children}
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};